import React, { isValidElement, memo, useCallback } from 'react';
import MergeChosenValue from './MergeChosenValue';
import styled from 'styled-components';
import { IExtendedMergeConflict, IMergeConflict } from './MergeConflicts';
import titleCase from '../../utils/TitleCase';
import { SelectButton } from './MergeConflictStyledComponents';
import { SuccessTheme } from '../../styles/themes/default/defaultFluentTheme';
import { InfoButton } from '../connectedForm/Button';
import { LockClosed16Regular, LockClosed24Regular, LockClosedRegular } from '@fluentui/react-icons';
import { Tooltip } from '@fluentui/react-components';

export const MergeFieldDiv = styled.div<{ pointer?: boolean; highlight?: boolean }>`
  display: inline-block;
  width: 25%;
  padding-left: 5px;
  border: 1px solid #e0e0e0;
  border-top: none;
  cursor: ${(props) => (props.pointer ? 'pointer' : 'auto')};
  word-wrap: break-word;
  min-height: 35px;
  vertical-align: top;
  font-weight: ${(props) => (props.highlight ? `bold` : 'normal')};
  background-color: ${(props) => (props.highlight ? `#f0fbf3` : 'undefined')};
`;

export const MergeFieldCentre = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 34px;
`;

const getDisplayValue = (object: any) => {
  let value = object;
  if (object === true) {
    value = 'checked';
  } else if (object === false) {
    value = 'unchecked';
  } else if (object === null) {
    value = '-';
  } else if (typeof object === 'string') {
    return object;
  } else if (Array.isArray(object)) {
    return (object as Array<any>).map((obj) => getDisplayValue(obj)).join(', ');
  } else if (typeof object === 'object') {
    if (object.description) {
      value = object.description;
    } else if (object.name) {
      value = object.name;
    } else if (object.reference) {
      value = object.reference;
    } else if (object.code) {
      value = object.code;
    } else if (object.label) {
      value = object.label;
    } else {
      value = object[Object.keys(object)[0]]; // get the first key in the object and returns the value temp fix.
    }
  } else if (!isValidElement(object)) {
    value = `${object}`;
  }

  if (!isValidElement(value) && typeof value !== 'string') {
    return `${value}`;
  }

  return value;
};

const MergeFieldOption = ({
  path,
  value,
  updateStore,
  highlight,
  locked,
}: {
  path: string;
  value: any;
  updateStore: (path: string, value: any) => void;
  highlight: boolean;
  locked?: boolean;
}) => {
  const onClickSelect = useCallback(() => {
    if (!locked) {
      updateStore(path, value);
    }
  }, [locked, path, updateStore, value]);

  return (
    <MergeFieldDiv pointer={!locked} onClick={onClickSelect} highlight={highlight}>
      <MergeFieldCentre>
        {getDisplayValue(value)}
        {!locked ? (
          <InfoButton
            data-testid={`select-field-${path}`}
            small={true}
            styles={{ root: { minHeight: '21px', height: '21px' } }}
            onClick={onClickSelect}
          >
            Select
          </InfoButton>
        ) : (
          <Tooltip
            content={
              'Locked - It is recommend to reload the page and re-enter changes rather than submitting this resolution.'
            }
            relationship={'label'}
          >
            <LockClosed24Regular />
          </Tooltip>
        )}
      </MergeFieldCentre>
    </MergeFieldDiv>
  );
};

const MergeField = ({
  conflict,
  form,
  updateStore,
  formValueHasChanged,
  locked,
}: {
  conflict: IExtendedMergeConflict;
  form: string;
  updateStore: (path: string, value: any) => void;
  formValueHasChanged: boolean;

  /**
   * If a merge field is 'locked' it should not be changeable - It is included for visibility only.
   */
  locked?: boolean;
}) => {
  const splitPath: Array<string> = conflict.path.split('.');
  const titleCasePath: Array<string> = splitPath.map((subPath) => titleCase(subPath));
  const rejoinedPath = titleCasePath.join(' - ');

  return (
    <div>
      <MergeFieldDiv>
        <MergeFieldCentre>{rejoinedPath}</MergeFieldCentre>
      </MergeFieldDiv>
      <MergeFieldOption
        path={conflict.path}
        value={conflict.formValue}
        highlight={formValueHasChanged}
        updateStore={updateStore}
        locked={locked}
      />
      <MergeFieldOption
        path={conflict.path}
        value={conflict.serverValue}
        highlight={!formValueHasChanged}
        updateStore={updateStore}
        locked={locked}
      />
      <MergeChosenValue form={form} path={conflict.path} getDisplayValue={getDisplayValue} conflict={conflict} />
    </div>
  );
};

export default memo(MergeField);
